import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Zap, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_PATH = '/workouts/25-for-25';

const buildEmbedSrc = (embedUrl, email) =>
  `${embedUrl}${embedUrl.includes('?') ? '&' : '?'}email=${encodeURIComponent(email)}`;

const BackLink = () => (
  <Link to="/" className="tf-back-link" data-testid="tf-back-link">
    <ArrowLeft size={14} /> gocalfit.com
  </Link>
);

export default function TwentyFivePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const canceled = searchParams.get('canceled');

  const [phase, setPhase] = useState('loading'); // loading | login | paywall | verifying | unlocked | comingsoon | payment_failed
  const [email, setEmail] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [buying, setBuying] = useState(false);
  const [justPurchased, setJustPurchased] = useState(false);
  const pollCount = useRef(0);

  const checkAccess = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/store/25-for-25/access`, { withCredentials: true });
      setEmail(data.email);
      if (!data.has_access) {
        if (data.checkout_enabled) setPhase('paywall');
        else navigate('/workouts', { replace: true });
      } else if (data.embed_url) {
        setEmbedUrl(data.embed_url);
        setPhase('unlocked');
      } else {
        setPhase('comingsoon');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          const { data: cfg } = await axios.get(`${BACKEND_URL}/api/store/25-for-25/config`);
          if (cfg.checkout_enabled) setPhase('login');
          else navigate('/workouts', { replace: true });
        } catch {
          navigate('/workouts', { replace: true });
        }
      } else {
        navigate('/workouts', { replace: true });
      }
    }
  }, [navigate]);

  const pollPayment = useCallback(async () => {
    if (pollCount.current >= 8) {
      setPhase('payment_failed');
      return;
    }
    pollCount.current += 1;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/store/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setJustPurchased(true);
        navigate(PAGE_PATH, { replace: true });
        checkAccess();
        return;
      }
      if (data.status === 'expired' || data.payment_status === 'failed') {
        setPhase('payment_failed');
        return;
      }
      setTimeout(pollPayment, 2000);
    } catch {
      setTimeout(pollPayment, 2000);
    }
  }, [sessionId, navigate, checkAccess]);

  useEffect(() => {
    if (sessionId) {
      setPhase('verifying');
      pollPayment();
    } else {
      checkAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async () => {
    setBuying(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/store/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data.already_owned) {
        checkAccess();
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      if (err.response?.status === 401) setPhase('login');
      setBuying(false);
    }
  };

  // ============ UNLOCKED: full-page iframe ============
  if (phase === 'unlocked') {
    return (
      <div className="tf-app-shell" data-testid="tf-app-shell">
        <div className="tf-app-bar">
          <BackLink />
          <span className="tf-app-bar-email" data-testid="tf-member-email">{email}</span>
        </div>
        <iframe
          src={buildEmbedSrc(embedUrl, email)}
          title="25 for 25 Workout Program"
          className="tf-app-iframe"
          allow="screen-wake-lock; fullscreen"
          data-testid="tf-app-iframe"
        />
      </div>
    );
  }

  return (
    <div className="tf-page" data-testid="tf-page">
      <div className="tf-topbar"><BackLink /></div>

      {phase === 'loading' && (
        <div className="tf-center" data-testid="tf-loading">
          <Loader2 className="tf-spin" size={32} />
        </div>
      )}

      {phase === 'verifying' && (
        <div className="tf-center" data-testid="tf-verifying">
          <Loader2 className="tf-spin" size={32} />
          <h2>Confirming your payment…</h2>
          <p>Hang tight — this usually takes a few seconds.</p>
        </div>
      )}

      {phase === 'login' && (
        <div className="tf-center" data-testid="tf-login-required">
          <div className="tf-badge"><Lock size={16} /> Members Only</div>
          <h1 className="tf-title">25 <span>FOR</span> 25</h1>
          <p className="tf-sub">Twenty-five workouts. Twenty-five minutes each. Log in to your GoCalFit account to continue.</p>
          <div className="tf-actions">
            <Link to={`/login?redirect=${encodeURIComponent(PAGE_PATH)}`} className="tf-btn-primary" data-testid="tf-login-btn">
              Log In
            </Link>
            <Link to="/signup" className="tf-btn-ghost" data-testid="tf-signup-link">
              Create an account
            </Link>
          </div>
        </div>
      )}

      {phase === 'paywall' && (
        <div className="tf-center" data-testid="tf-paywall">
          <div className="tf-badge"><Zap size={16} /> One-Time Purchase</div>
          <h1 className="tf-title">25 <span>FOR</span> 25</h1>
          <p className="tf-sub">
            25 workouts. 25 minutes each. $25 — once, and it's yours forever.
            Your progress follows you on every device.
          </p>
          {canceled && <p className="tf-note" data-testid="tf-canceled-note">Checkout canceled — no charge was made.</p>}
          <ul className="tf-perks">
            <li><CheckCircle2 size={16} /> Lifetime access — no subscription</li>
            <li><CheckCircle2 size={16} /> Progress tracked across all your devices</li>
            <li><CheckCircle2 size={16} /> Built for busy schedules: 25 minutes, done</li>
          </ul>
          <button className="tf-btn-primary" onClick={startCheckout} disabled={buying} data-testid="tf-buy-btn">
            {buying ? 'Redirecting…' : 'Get Access for $25'}
          </button>
          <p className="tf-signed-in">Signed in as {email}</p>
        </div>
      )}

      {phase === 'payment_failed' && (
        <div className="tf-center" data-testid="tf-payment-failed">
          <h2>We couldn't confirm that payment</h2>
          <p className="tf-sub">If you were charged, your access will unlock automatically within a few minutes. Otherwise, you can try again.</p>
          <button className="tf-btn-primary" onClick={() => { pollCount.current = 0; checkAccess(); }} data-testid="tf-retry-btn">
            Check again
          </button>
        </div>
      )}

      {phase === 'comingsoon' && (
        <div className="tf-center" data-testid="tf-coming-soon">
          {justPurchased && <div className="tf-badge tf-badge-success"><CheckCircle2 size={16} /> Payment confirmed</div>}
          <h1 className="tf-title">You're <span>IN</span>.</h1>
          <p className="tf-sub">
            Your 25 for 25 access is locked in forever. The workout app is getting its final polish —
            it will appear right here the moment it goes live. No extra steps needed.
          </p>
          <p className="tf-signed-in">Access granted to {email}</p>
        </div>
      )}
    </div>
  );
}
